import type { UseQueryResult } from "react-query";
import { useQuery } from "react-query";

import apiBase from "../../utils/apiBase";

export type Period = "minute" | "hour" | "day" | "week" | "month";

export interface IAnalyticsQuery {
  dateFilter?: null | {
    fromDate: Date;
    toDate?: Date;
  };
  period?: Period;
  applicationId?: string;
}

export interface IRequestsItem {
  cacheType: string;
  httpStatusCode: number;
  count: number;
}

export interface ITokensItem {
  cacheType: string;
  count: number;
  model: string;
}

export interface IAnalyticsResponse {
  cacheHitRatios?: {
    date: string;
    cumulativeCacheHitRatio: number;
    instantCacheHitRatio: number;
  }[];

  requests?: {
    date: string;
    items: IRequestsItem[];
  }[];
  latency?: {
    date: string;
    averageLatency: number;
  }[];
  tokens?: {
    date: string;
    items: ITokensItem[];
  }[];
  prices?: {
    date: string;
    costs: number;
    savings: number;
  }[];
}

const fetchAnalytics = async (
  organizationId?: string,
  query?: IAnalyticsQuery,
): Promise<IAnalyticsResponse> => {
  return await apiBase.get(`/organizations/${organizationId}/analytics`, {
    params: {
      fromDate: query?.dateFilter?.fromDate,
      toDate: query?.dateFilter?.toDate,
      applicationId: query?.applicationId,
      period: query?.period,
    },
  });
};

export const useAnalytics = (
  organizationId: string,
  query: IAnalyticsQuery,
): UseQueryResult<IAnalyticsResponse, unknown> => {
  return useQuery(
    [
      "analytics",
      organizationId,
      query.applicationId,
      query.dateFilter,
      query.period,
    ],
    () => {
      return fetchAnalytics(organizationId, query);
    },
    {
      enabled: false,
    },
  );
};
